import React, {useEffect, useState} from 'react';
import {useIntl} from "react-intl";
import {SiteDto} from "../../../../../modules/vms/dto/SiteDto";
import {SiteApi} from "../../../../../modules/vms/api/SiteApi";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {MessageUtils} from "../../../../../utils/MessageUtils";
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import FormFieldText from "../../../../../components/FormFieldText";
import SaveButton from "../../../../../components/buttons/SaveButton";
import CancelButton from "../../../../../components/buttons/CancelButton";
import DataGroupSelectDialog from "../../../../account/dataGroup/manage/components/DataGroupSelectDialog";
import SearchIconButton from "../../../../../components/buttons/SearchIconButton";
import {DataGroupDto} from "../../../../../modules/account/dto/DataGroupDto";
import {DataGroupApi} from "../../../../../modules/account/api/DataGroupApi";

export type SiteDetailDialogProps = {
    open: boolean;
    site: SiteDto;
    onClose: () => void;
    onOk: () => void;
};

const SiteDetailDialog: React.FC<SiteDetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.site.id ? 'VMS_SITE_MODIFY' : 'VMS_SITE_CREATE';

    const [innerSite, setInnerSite] = useState<SiteDto>(props.site);

    // 数据组
    const [innerUserDataGroupName, setInnerUserDataGroupName] = useState<string>();
    const [dataGroups, setDataGroups] = useState<Array<DataGroupDto>>([]);
    const [openDataGroupSelectDialog, setOpenDataGroupSelectDialog] = useState(false);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    useEffect(() => {
        setInnerSite({...props.site});
        fetchDataGroups();

        // 如果当前站点有数据组，则显示数据组名字
        if (props.site.dataGroupId) {
            getDataGroupParentName(props.site.dataGroupId, dataGroups);
        }
    }, [props.site]);

    const fetchDataGroups = async () => {
        const responseResult = await DataGroupApi.getDataGroupList();
        if (responseResult && responseResult.status === HttpStatus.SUCCESS) {
            setDataGroups(DataGroupApi.constructDataGroupTree(responseResult.data));
        }
    };

    const handleChange = (name: string, value: any) => {
        setInnerSite(previousSite => ({...previousSite, [name]: value}));
        if (name === 'dataGroupId') {
            getDataGroupParentName(value, dataGroups);
        }
    };

    const handleOk = async () => {
        if (validate()) {
            if (props.site.id) {
                const responseResult = await SiteApi.updateSite(innerSite);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'VMS_SITE_MODIFY_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await SiteApi.createSite(innerSite);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'VMS_SITE_CREATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const handleCancel = () => {
        props.onClose();
    };

    const validate = () => {
        const newMessageMap = new Map<string, string>();
        if (innerSite.code === '') {
            newMessageMap.set('code', intl.formatMessage({id: 'VMS_SITE_CODE_REQUIRED'}));
        }
        if (innerSite.name === '') {
            newMessageMap.set('name', intl.formatMessage({id: 'VMS_SITE_NAME_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    const getDataGroupParentName = (id: string, dataGroups: Array<DataGroupDto>) => {
        if (id) {
            for (let item of dataGroups) {
                if (item.id === id) {
                    setInnerUserDataGroupName(item.name);
                    return;
                } else if (item.children && item.children.length > 0) {
                    getDataGroupParentName(id, item.children);
                }
            }
        } else {
            return '';
        }
    }

    return (
        <Dialog open={props.open} onClose={handleCancel} fullWidth={true}>
            <DialogTitle>{intl.formatMessage({id: title})}</DialogTitle>
            <DialogContent>
                <FormFieldText label={intl.formatMessage({id: "VMS_SITE_CODE"})} value={innerSite.code} required={true}
                               errorText={messageMap.get("code")}
                               onChange={(value) => handleChange('code', value)}/>
                <FormFieldText label={intl.formatMessage({id: "VMS_SITE_NAME"})} value={innerSite.name} required={true}
                               errorText={messageMap.get("name")}
                               onChange={(value) => handleChange('name', value)}/>
                <FormFieldText label={intl.formatMessage({id: "VMS_SITE_SEQUENCE"})} value={innerSite.sequence}
                               type={'number'}
                               onChange={(value) => handleChange('sequence', value)}/>
                <DataGroupSelectDialog dataGroups={dataGroups} open={openDataGroupSelectDialog}
                                       onSelect={(value) => handleChange("dataGroupId", value)}
                                       onClose={() => setOpenDataGroupSelectDialog(false)}/>
                <FormFieldText label={intl.formatMessage({id: "VMS_SITE_DATA_GROUP"})}
                               value={innerUserDataGroupName}
                               disabled={true}
                               endAdornment={<SearchIconButton onClick={() => setOpenDataGroupSelectDialog(true)}/>}/>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={handleCancel}/>
                <SaveButton onClick={handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default SiteDetailDialog;
